import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  useMediaQuery,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { FiPower } from "react-icons/fi";
import { FaRegFlag } from "react-icons/fa";
import { MdAccountCircle, MdLogout } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import apiEndpoints from "../apiconfig";

const Header = ({ isMobile, toggleSidebar }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const [user, setUser] = useState({
    name: "",
    profileImage: "",
  });

  const [wabaData, setWabaData] = useState({
    number: "",
    status: "Live",
    quality: "GREEN",
    tier: "MSG_1000 LIMIT",
  });

  useEffect(() => {
    const token = localStorage.getItem("token") || "";
    if (!token) return;

    axios
      .post(
        apiEndpoints.getProfile,
        { action: "getProfile" },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        const payload = res.data || {};
        if (payload.status === "success" && payload.user) {
          const u = payload.user;
          const displayName = u.primary_contact_name;
          const profileImage =
            u.profile_image ||
            u.profileImage ||
            u.avatar ||
            "https://via.placeholder.com/40";
          const rawWaba =
            u.wabaNumber ||
            u.waba_number ||
            u.mobile_number ||
            u.mobile ||
            u.phone ||
            u.mobile_no ||
            "";
          const wabaNumber = String(rawWaba).replace(/\D/g, "");
          setUser({ name: displayName, profileImage });
          setWabaData({
            number: wabaNumber || "",
            status: u.waba_status || "Live",
            quality: u.waba_quality || "GREEN",
            tier: u.waba_tier || "MSG_1000 LIMIT",
          });
        }
      })
      .catch((err) => console.error("Error fetching profile for header:", err));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  // Format phone number for better display
  const formatPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length <= 10) {
      return `+91${cleaned}`;
    }
    return `+${cleaned}`;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        padding: {
          xs: "8px 12px",
          sm: "12px 16px",
          md: "16px 24px",
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontFamily: "Montserrat, sans-serif",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderBottom: "1px solid #E5E7EB",
        minHeight: { xs: "56px", sm: "64px" },
      }}
    >
      {/* LEFT SIDE: Menu Button + Welcome Message */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
        {/* Mobile Menu Button - Show on all mobile devices */}
        <IconButton
          onClick={toggleSidebar}
          sx={{
            padding: { xs: "6px", sm: "8px" },
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            backgroundColor: "#F8FAFC",
            "&:hover": {
              backgroundColor: "#F1F5F9",
            },
            display: { sm: "block", md: "none" }, // visible below 768px
            // Only show on mobile
          }}
        >
          <MenuIcon
            sx={{
              fontSize: { xs: 18, sm: 20 },
              color: "#374151",
            }}
          />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: "8px", sm: "16px", md: "24px" },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: { sm: "12px", md: "20px" },
            flexWrap: "wrap",
          }}
        >
          {/* WABA Number */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FaRegFlag size={16} style={{ color: "#25D366" }} />
            <Typography
              sx={{
                fontWeight: 600,
                color: "#1E293B",
                fontSize: { sm: "13px", md: "14px" },
                whiteSpace: "nowrap",
              }}
            >
              WABA:{" "}
              <span
                style={{
                  color: "#4B5563",
                  fontWeight: 500,
                  marginLeft: "4px",
                  fontSize: { sm: "12px", md: "13px" },
                }}
              >
                {formatPhoneNumber(wabaData.number).slice(0, 12)}
                {formatPhoneNumber(wabaData.number).length > 12 ? "..." : ""}
              </span>
            </Typography>
          </Box>

          {/* Status - Hide on small tablet */}
          <Box
            sx={{
              display: { sm: "flex", md: "flex" },
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FiPower size={16} style={{ color: "#F59E0B" }} />
            <Typography
              sx={{
                fontWeight: 600,
                color: "#1E293B",
                fontSize: { sm: "13px", md: "14px" },
                whiteSpace: "nowrap",
              }}
            >
              Status:{" "}
              <span
                style={{
                  color: wabaData.status === "Live" ? "#25D366" : "#EF4444",
                  fontWeight: 600,
                  marginLeft: "4px",
                  fontSize: { sm: "12px", md: "13px" },
                }}
              >
                {wabaData.status}
              </span>
            </Typography>
          </Box>
        </Box>

        {/* User Profile */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* User Name - Hide on very small screens */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "13px", sm: "14px" },
              color: "#111827",
              display: { xs: "none", sm: "block" },
              maxWidth: { xs: "80px", sm: "120px" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user.name}
          </Typography>

          <IconButton
            onClick={handleProfileClick}
            sx={{
              border: "2px solid #E5E7EB",
              borderRadius: "50%",
              padding: "2px",
              "&:hover": {
                borderColor: "#25D366",
                backgroundColor: "rgba(37, 211, 102, 0.05)",
              },
            }}
          >
            <Avatar
              src={user.profileImage}
              sx={{
                width: { xs: 32, sm: 36, md: 38 },
                height: { xs: 32, sm: 36, md: 38 },
                border: "2px solid #25D366",
                transition: "transform 0.2s",
                "&:hover": { transform: "scale(1.05)" },
              }}
            />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: {
                width: 200,
                borderRadius: "10px",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                marginTop: "8px",
                border: "1px solid #E5E7EB",
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              onClick={() => {
                handleClose();
                navigate("/profile");
              }}
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 500,
                padding: "10px 16px",
                "&:hover": {
                  backgroundColor: "rgba(37, 211, 102, 0.1)",
                },
              }}
            >
              <MdAccountCircle
                size={18}
                style={{ marginRight: 10, color: "#25D366" }}
              />
              Profile
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            <MenuItem
              onClick={() => {
                handleClose();
                handleLogout();
              }}
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 500,
                color: "#EF4444",
                padding: "10px 16px",
                "&:hover": {
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                },
              }}
            >
              <MdLogout size={18} style={{ marginRight: 10 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
